import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Nav, NavItem, NavLink, Row } from "reactstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import {
  buildVelzonDataTableStyles,
  ensureVelzonDataTableThemes,
} from "../../../../Components/Common/dataTableTheme";

ensureVelzonDataTableThemes();

export type InventoryStatusTab = {
  id: string;
  label: string;
  badgeCount?: number;
  badgeColor?: string;
};

interface InventoryTableTabsProps {
  tabs: InventoryStatusTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  columns: TableColumn<any>[];
  data: any[];
  selectionCount?: number;
  onBulkRemove?: () => void;
  selectionLabel?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  renderActions?: (tabId: string) => React.ReactNode;
}

const InventoryTableTabs = ({
  tabs,
  activeTab,
  onTabChange,
  columns,
  data,
  selectionCount = 0,
  onBulkRemove,
  selectionLabel = "Resultados",
  emptyMessage = "No encontramos registros para esta vista.",
  searchPlaceholder = "Buscar…",
  renderActions,
}: InventoryTableTabsProps) => {
  const showSelection = selectionCount > 0 && onBulkRemove;
  const tableKey = useMemo(() => `inventory-table-${activeTab}`, [activeTab]);
  const [searchValue, setSearchValue] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [themeMode, setThemeMode] = useState<"light" | "dark">(
    typeof document !== "undefined" && document.body.getAttribute("data-bs-theme") === "dark" ? "dark" : "light",
  );

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const target = document.body;
    const updateMode = () => {
      setThemeMode(target.getAttribute("data-bs-theme") === "dark" ? "dark" : "light");
    };
    updateMode();
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.attributeName === "data-bs-theme")) {
        updateMode();
      }
    });
    observer.observe(target, { attributes: true, attributeFilter: ["data-bs-theme"] });
    return () => observer.disconnect();
  }, []);

  const dataTableStyles = useMemo(() => buildVelzonDataTableStyles(themeMode), [themeMode]);

  const filteredData = useMemo(() => {
    if (!searchValue.trim()) {
      return data;
    }
    const needle = searchValue.toLowerCase();
    return data.filter((row) => {
      if (!row || typeof row !== "object") {
        return false;
      }
      return Object.values(row).some((value) => {
        if (value == null) return false;
        if (typeof value === "string") {
          return value.toLowerCase().includes(needle);
        }
        if (typeof value === "number") {
          return value.toString().includes(needle);
        }
        if (Array.isArray(value)) {
          return value.some((item) => String(item ?? "").toLowerCase().includes(needle));
        }
        return String(value).toLowerCase().includes(needle);
      });
    });
  }, [data, searchValue]);

  useEffect(() => {
    setResetPaginationToggle((prev) => !prev);
  }, [searchValue, activeTab]);

  return (
    <Card>
      <CardHeader className="border-0 pb-0">
        <Row className="align-items-center g-3">
          <Col>
            <Nav className="nav-tabs-custom card-header-tabs border-bottom-0" role="tablist">
              {tabs.map((tab) => (
                <NavItem key={tab.id}>
                  <NavLink
                    href="#"
                    className={classNames("fw-semibold", { active: activeTab === tab.id })}
                    onClick={(event) => {
                      event.preventDefault();
                      onTabChange(tab.id);
                    }}
                  >
                    {tab.label}
                    {typeof tab.badgeCount === "number" ? (
                      <span
                        className={`badge ${
                          tab.badgeColor || "bg-secondary-subtle text-secondary"
                        } align-middle rounded-pill ms-2`}
                      >
                        {tab.badgeCount}
                      </span>
                    ) : null}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </Col>
          <Col xs="auto" className="d-flex align-items-center gap-3">
            <FormGroup className="mb-0 d-flex align-items-center">
              <Label className="me-2 mb-0">Filas</Label>
              <Input
                type="select"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
                className="form-select form-select-sm"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Input>
            </FormGroup>
            {showSelection ? (
              <div className="d-flex align-items-center text-muted">
                Seleccionaste
                <span className="text-body fw-semibold px-1">{selectionCount}</span>
                {selectionLabel}
                <button type="button" className="btn btn-link link-danger p-0 ms-3" onClick={onBulkRemove}>
                  Quitar
                </button>
              </div>
            ) : null}
          </Col>
        </Row>
      </CardHeader>
      <CardBody className="pt-0">
        <div className="d-flex flex-column flex-lg-row gap-3 align-items-stretch mb-3 mt-3">
          <div className="flex-grow-1">
            <Input
              type="search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder={searchPlaceholder}
              className="form-control"
            />
          </div>
          <div className="d-flex justify-content-lg-end gap-2 flex-wrap">
            {renderActions ? renderActions(activeTab) : null}
          </div>
        </div>
        {filteredData.length > 0 ? (
          <DataTable
            key={tableKey}
            columns={columns}
            data={filteredData}
            dense
            highlightOnHover
            striped
            pagination
            paginationPerPage={pageSize}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            paginationComponentOptions={{ rowsPerPageText: "Filas por página", rangeSeparatorText: "de" }}
            onChangeRowsPerPage={(newPerPage) => setPageSize(newPerPage)}
            paginationResetDefaultPage={resetPaginationToggle}
            customStyles={dataTableStyles}
            theme={themeMode === "dark" ? "velzonDark" : "velzonLight"}
            persistTableHead
            noDataComponent={<div className="py-4 text-center text-muted">{emptyMessage}</div>}
          />
        ) : (
          <div className="py-4 text-center text-muted">{emptyMessage}</div>
        )}
      </CardBody>
    </Card>
  );
};

export default InventoryTableTabs;
